import { __awaiter } from "tslib";
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from './types';
import { addItem, calcCartCounts, decrementItem, getUserTimezone, incrementItem, makeFirstRequestedAt, makeFirstTimes, makeRandomNumberString, makeRequestedAtStr, rehydrateCart, rehydrateCheckoutForm, removeItem, timezoneMap } from '@open-tender/utils';
import { updateForm } from './checkout';
import { resetMenuVars } from './menu';
import { setMenuItems } from './menuItems';
const initialState = {
    address: null,
    alert: null,
    cart: [],
    cartCounts: {},
    currentCategory: null,
    currentItem: null,
    currentVendor: null,
    deviceType: null,
    error: null,
    isCurbside: false,
    isOutpost: false,
    loading: 'idle',
    messages: [],
    orderId: null,
    prepType: null,
    orderType: null,
    requestedAt: 'asap',
    revenueCenter: null,
    serviceType: null,
    table: null
};
export var OrderActionType;
(function (OrderActionType) {
    OrderActionType["FetchRevenueCenter"] = "order/fetchRevenueCenter";
    OrderActionType["FetchLocation"] = "order/fetchLocation";
    OrderActionType["RevertMenu"] = "order/revertMenu";
    OrderActionType["RefreshRevenueCenter"] = "order/refreshRevenueCenter";
    OrderActionType["EditOrder"] = "order/editOrder";
    OrderActionType["ReorderPastOrder"] = "order/reorderPastOrder";
})(OrderActionType || (OrderActionType = {}));
export const fetchRevenueCenter = createAsyncThunk(OrderActionType.FetchRevenueCenter, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const revenueCenter = yield api.getRevenueCenter(requestData.revenueCenterId, requestData.requestedAt);
        dispatch(setRevenueCenter(revenueCenter));
        return revenueCenter;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const fetchLocation = createAsyncThunk(OrderActionType.FetchLocation, ({ revenueCenterId, cartTotal }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const revenueCenter = yield api.getLocation(revenueCenterId, cartTotal);
        dispatch(setRevenueCenter(revenueCenter));
        return revenueCenter;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const fetchLocationBySlug = createAsyncThunk(OrderActionType.FetchLocation, ({ slug, cartTotal }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const revenueCenter = yield api.getLocationBySlug(slug, cartTotal);
        dispatch(setRevenueCenter(revenueCenter));
        return revenueCenter;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const revertMenu = createAsyncThunk(OrderActionType.RevertMenu, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const revenueCenter = yield api.getLocation(requestData.revenueCenterId);
        dispatch(setMenuVars({
            revenueCenter,
            serviceType: requestData.serviceType,
            requestedAt: requestData.requestedAt
        }));
        return revenueCenter;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const refreshRevenueCenter = createAsyncThunk(OrderActionType.RefreshRevenueCenter, ({ revenueCenterId, serviceType, requestedAt, reset }, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const revenueCenter = yield api.getLocation(revenueCenterId);
        if (reset)
            requestedAt = null;
        const firstTimes = makeFirstTimes(revenueCenter, serviceType, requestedAt);
        const { status } = revenueCenter;
        if (!firstTimes || status !== 'OPEN') {
            return { type: 'closed', args: { status, preventClose: true } };
        }
        else {
            return {
                type: 'adjustRequestedAt',
                args: { firstTimes, revenueCenter, preventClose: true }
            };
        }
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const editOrder = createAsyncThunk(OrderActionType.EditOrder, (order, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    const alert = { type: 'working', args: { text: 'Building your order...' } };
    dispatch(setAlert(alert));
    try {
        const { order_id: orderId, order_type: orderType, service_type: serviceType, requested_at: requestedAt, revenue_center, address } = order;
        const revenueCenterId = revenue_center === null || revenue_center === void 0 ? void 0 : revenue_center.revenue_center_id;
        const revenueCenter = yield api.getLocation(revenueCenterId);
        const menuItems = yield api.getMenuItems(revenueCenterId, serviceType);
        const { cart, cartCounts } = rehydrateCart(menuItems, order.cart);
        dispatch(setMenuItems(menuItems));
        const form = rehydrateCheckoutForm(order);
        dispatch(updateForm(form));
        // dispatch(toggleSidebar())
        const isOutpost = revenueCenter.is_outpost;
        const payload = {
            orderId,
            orderType,
            serviceType,
            isOutpost,
            revenueCenter,
            requestedAt,
            address,
            cart: cart,
            cartCounts
        };
        dispatch(setAlert({ type: 'closeAndSidebar' }));
        return payload;
    }
    catch (err) {
        dispatch(resetAlert());
        dispatch(addMessage('Something went wrong. Please contact support.'));
        return rejectWithValue(null);
    }
}));
export const reorderPastOrder = createAsyncThunk(OrderActionType.ReorderPastOrder, ({ revenueCenterId, serviceType, items }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    const alert = { type: 'working', args: { text: 'Building your order...' } };
    dispatch(setAlert(alert));
    try {
        const revenueCenter = yield api.getLocation(revenueCenterId);
        const requestedAt = makeFirstRequestedAt(revenueCenter, serviceType, null);
        if (!requestedAt) {
            const { status } = revenueCenter;
            const alert = { type: 'closed', args: { status, isCancel: true } };
            dispatch(setAlert(alert));
            return {};
        }
        else {
            dispatch(resetMenuVars());
            dispatch(resetRevenueCenter());
            const menuItems = yield api.getMenuItems(revenueCenterId, serviceType);
            const { cart, cartCounts } = rehydrateCart(menuItems, items);
            dispatch(setMenuItems(menuItems));
            const orderType = revenueCenter.revenue_center_type;
            const payload = {
                revenueCenter,
                requestedAt,
                serviceType,
                cart,
                cartCounts
            };
            const alert = {
                type: 'requestedAt',
                args: {
                    openSidebar: true,
                    focusFirst: true,
                    skipClose: true,
                    isReorder: true,
                    revenueCenter,
                    serviceType,
                    orderType,
                    requestedAt
                }
            };
            dispatch(setAlert(alert));
            return payload;
        }
    }
    catch (err) {
        dispatch(resetAlert());
        dispatch(addMessage('Something went wrong. Please contact support.'));
        return rejectWithValue(null);
    }
}));
const orderSlice = createSlice({
    name: ReducerType.Order,
    initialState,
    reducers: {
        resetOrder: () => initialState,
        resetOrderType: state => {
            state.orderId = null;
            state.orderType = null;
            state.serviceType = null;
            state.prepType = null;
            state.isOutpost = false;
            state.isCurbside = false;
            state.revenueCenter = null;
            state.table = null;
            state.requestedAt = null;
        },
        resetRevenueCenter: state => {
            state.revenueCenter = null;
        },
        resetLocation: state => {
            state.revenueCenter = null;
        },
        resetCart: state => {
            state.cart = null;
            state.cartCounts = null;
        },
        resetMessages: state => {
            state.messages = [];
        },
        resetAlert: state => {
            state.alert = null;
        },
        updateOrder: (state, action) => {
            return Object.assign(Object.assign({}, state), action.payload);
        },
        checkout: state => state,
        setAlert: (state, action) => {
            state.alert = action.payload;
        },
        setOrderId: (state, action) => {
            state.orderId = action.payload;
        },
        setOrderType: (state, action) => {
            state.orderType = action.payload;
        },
        setServiceType: (state, action) => {
            state.serviceType = action.payload;
        },
        setOrderServiceType: (state, action) => {
            return Object.assign(Object.assign({}, state), action.payload);
        },
        setCurbside: (state, action) => {
            state.isCurbside = action.payload;
        },
        setDeviceType: (state, action) => {
            state.deviceType = action.payload;
        },
        setCurrentVendor: (state, action) => {
            state.currentVendor = action.payload;
        },
        setMenuVars: (state, action) => {
            const { revenueCenter } = action.payload;
            return Object.assign(Object.assign(Object.assign({}, state), action.payload), { orderType: revenueCenter.revenue_center_type, isOutpost: revenueCenter.is_outpost });
        },
        setAddress: (state, action) => {
            state.address = action.payload;
        },
        setRequestedAt: (state, action) => {
            const messages = state.messages.filter(i => !i.message.includes('Requested time'));
            state.requestedAt = action.payload;
            state.messages = messages;
        },
        setRevenueCenter: (state, action) => {
            const revenueCenter = action.payload;
            const previousRequestedAt = state.requestedAt;
            const requestedAt = makeFirstRequestedAt(revenueCenter, state.serviceType, previousRequestedAt);
            let messages;
            if (previousRequestedAt && requestedAt !== previousRequestedAt) {
                const otherMessages = state.messages.filter(i => !i.message.includes('Requested time'));
                const tz = timezoneMap[revenueCenter.timezone];
                const requestedAtText = makeRequestedAtStr(requestedAt, tz);
                const msg = {
                    message: `Requested time updated to ${requestedAtText}`,
                    id: makeRandomNumberString()
                };
                messages = [msg, ...otherMessages];
            }
            else {
                messages = state.messages;
            }
            state.revenueCenter = revenueCenter;
            state.requestedAt = requestedAt !== null && requestedAt !== void 0 ? requestedAt : null;
            state.messages = messages;
        },
        setTable: (state, action) => {
            state.table = action.payload;
        },
        setPrepType: (state, action) => {
            state.prepType = action.payload;
        },
        setCart: (state, action) => {
            const cartCounts = calcCartCounts(action.payload);
            state.cart = action.payload;
            state.cartCounts = cartCounts;
        },
        setCurrentCategory: (state, action) => {
            state.currentCategory = action.payload;
        },
        setCurrentItem: (state, action) => {
            state.currentItem = action.payload;
        },
        addItemToCart: (state, action) => {
            const currentCart = state.cart !== null ? state.cart : [];
            const { cart, cartCounts } = addItem([...currentCart], action.payload);
            state.cart = cart;
            state.cartCounts = cartCounts;
        },
        removeItemFromCart: (state, action) => {
            const { index } = action.payload;
            const currentCart = state.cart !== null ? state.cart : [];
            const { cart, cartCounts } = removeItem([...currentCart], index);
            state.cart = cart;
            state.cartCounts = cartCounts;
        },
        incrementItemInCart: (state, action) => {
            const { index } = action.payload;
            const currentCart = state.cart !== null ? state.cart : [];
            const { cart, cartCounts } = incrementItem([...currentCart], index);
            state.cart = cart;
            state.cartCounts = cartCounts;
        },
        decrementItemInCart: (state, action) => {
            const { index } = action.payload;
            const currentCart = state.cart !== null ? state.cart : [];
            const { cart, cartCounts } = decrementItem([...currentCart], index);
            state.cart = cart;
            state.cartCounts = cartCounts;
        },
        addMessage: (state, action) => {
            const existing = state.messages.map(i => i.message);
            if (existing.includes(action.payload)) {
                // do nothing
                return;
            }
            state.messages = [
                ...state.messages,
                {
                    message: action.payload,
                    id: makeRandomNumberString()
                }
            ];
        },
        removeMessage: (state, action) => {
            state.messages = state.messages.filter(i => i.id !== action.payload);
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchRevenueCenter.fulfilled, state => {
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchRevenueCenter.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchRevenueCenter.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(fetchLocation.fulfilled, state => {
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchLocation.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchLocation.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(revertMenu.fulfilled, state => {
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(revertMenu.pending, state => {
            state.loading = 'pending';
        })
            .addCase(revertMenu.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(refreshRevenueCenter.fulfilled, (state, action) => {
            state.alert = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(refreshRevenueCenter.pending, state => {
            state.loading = 'pending';
        })
            .addCase(refreshRevenueCenter.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(editOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), action.payload), { loading: 'idle', error: null });
        })
            .addCase(editOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(editOrder.rejected, state => {
            state.loading = 'idle';
        })
            .addCase(reorderPastOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), action.payload), { loading: 'idle', error: null });
        })
            .addCase(reorderPastOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(reorderPastOrder.rejected, state => {
            state.loading = 'idle';
        });
    }
});
export const { resetOrder, addItemToCart, addMessage, decrementItemInCart, incrementItemInCart, removeItemFromCart, removeMessage, resetAlert, resetCart, resetLocation, resetMessages, resetOrderType, resetRevenueCenter, setAddress, setAlert, setCart, setCurbside, setCurrentCategory, setCurrentItem, setDeviceType, setMenuVars, setOrderId, setOrderServiceType, setOrderType, setPrepType, setRequestedAt, setRevenueCenter, setServiceType, setTable, checkout, setCurrentVendor, updateOrder } = orderSlice.actions;
export const selectAlert = (state) => state.order.alert;
export const selectTimezone = (state) => {
    var _a;
    return state.order.revenueCenter
        ? timezoneMap[(_a = state.order.revenueCenter.timezone) !== null && _a !== void 0 ? _a : 'US/Central']
        : getUserTimezone();
};
export const selectOrder = (state) => state.order;
export const selectCartQuantity = (state) => {
    return state.order.cart
        ? state.order.cart.reduce((t, i) => (t += i.quantity), 0)
        : 0;
};
export const selectCartTotal = (state) => {
    return state.order.cart
        ? state.order.cart.reduce((t, i) => (t += i.totalPrice || 0), 0.0)
        : 0.0;
};
export const selectCartCounts = (state) => state.order.cartCounts || {};
export const selectCanOrder = (state) => state.order.revenueCenter &&
    state.order.serviceType &&
    state.order.requestedAt;
export const selectOrderLimits = createSelector((state) => {
    const { revenueCenter, serviceType } = state.order;
    const { cartGuest, spendingLimit } = state.groupOrder;
    return { revenueCenter, serviceType, cartGuest, spendingLimit };
}, ({ revenueCenter, serviceType, cartGuest, spendingLimit }) => {
    if (!revenueCenter || !serviceType) {
        return { orderMinimum: null, orderMaximum: null };
    }
    const { order_maximum, order_minimum } = revenueCenter;
    const orderMax = cartGuest && spendingLimit
        ? parseFloat(spendingLimit)
        : order_maximum
            ? parseFloat(order_maximum[serviceType])
            : 0;
    const orderMaximum = orderMax;
    const orderMinimum = order_minimum
        ? parseFloat(order_minimum[serviceType])
        : 0;
    return {
        orderMinimum: orderMinimum > 0 ? orderMinimum : null,
        orderMaximum: orderMaximum > 0 ? orderMaximum : null
    };
});
export const selectCartIds = createSelector((state) => {
    return state.order.cart;
}, cart => (cart ? cart.map(i => i.id) : []));
export const selectMenuSlug = (state) => {
    const { revenueCenter } = state.order;
    return revenueCenter ? `/menu/${revenueCenter.slug}` : '/';
};
export const selectRevenueCenter = (state) => state.order.revenueCenter;
export const selectCart = (state) => state.order.cart;
export const selectCurrentVendor = (state) => state.order.currentVendor;
export const selectMessages = (state) => state.order.messages;
export const selectCurrentItem = (state) => state.order.currentItem;
export const selectMenuVars = createSelector((state) => {
    const { revenueCenter, serviceType, requestedAt } = state.order;
    return { revenueCenter, serviceType, requestedAt };
}, ({ revenueCenter, serviceType, requestedAt }) => {
    return {
        revenueCenterId: (revenueCenter === null || revenueCenter === void 0 ? void 0 : revenueCenter.revenue_center_id) || null,
        serviceType: serviceType,
        requestedAt: requestedAt
    };
});
export const selectAutoSelect = (state) => {
    var _a, _b;
    const autoSelect = (_b = (_a = state.config) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.autoSelect;
    const { orderType, serviceType } = state.order;
    return autoSelect && orderType && serviceType
        ? autoSelect[orderType][serviceType]
        : false;
};
export const selectCurrentCategory = (state) => state.order.currentCategory;
export const orderReducer = orderSlice.reducer;
